import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  listAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  type ManagedAddress,
} from "@/lib/addresses.functions";
import { listMyAddresses, type SavedAddress } from "@/lib/orders.functions";
import { useAuth } from "@/hooks/use-auth";
import { normalizePhoneDigits, isValidContactPhone } from "@/lib/phone";

export const Route = createFileRoute("/_authenticated/account/addresses")({
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/account/addresses" },{ title: "Οι διευθύνσεις μου — MrPanda.gr" }] }),
  component: AddressesPage,
});

type FormState = {
  id?: string;
  label: string;
  fullName: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  region: string;
  country: string;
  isDefault: boolean;
};

const EMPTY: FormState = {
  label: "",
  fullName: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  postalCode: "",
  region: "",
  country: "Ελλάδα",
  isDefault: false,
};

function AddressesPage() {
  const { user } = useAuth();
  const listFn = useServerFn(listAddresses);
  const createFn = useServerFn(createAddress);
  const updateFn = useServerFn(updateAddress);
  const deleteFn = useServerFn(deleteAddress);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({ queryKey: ["my-addresses"], queryFn: () => listFn(), enabled: !!user });
  const addresses = (data?.addresses ?? []) as ManagedAddress[];

  const derivedFn = useServerFn(listMyAddresses);
  const { data: derivedData } = useQuery({ queryKey: ["my-order-addresses"], queryFn: () => derivedFn(), enabled: !!user });
  const saved = new Set(
    addresses.map((a) => `${a.line1}|${a.city}|${a.postalCode}`.toLowerCase()),
  );
  const derived = ((derivedData?.addresses ?? []) as SavedAddress[]).filter(
    (a) => !saved.has(`${a.line1}|${a.city}|${a.postalCode}`.toLowerCase()),
  );

  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["my-addresses"] });

  const saveMut = useMutation({
    mutationFn: (f: FormState) =>
      f.id
        ? updateFn({ data: { ...f, id: f.id } })
        : createFn({ data: f }),
    onSuccess: () => {
      invalidate();
      setForm(null);
      setError(null);
    },
    onError: (_e, f) =>
      setError(
        /^\d{10}$/.test(f.phone)
          ? "Κάτι πήγε στραβά κατά την αποθήκευση. Δοκίμασε ξανά."
          : "Έλεγξε τα στοιχεία — το τηλέφωνο χρειάζεται 10 ψηφία.",
      ),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: invalidate,
  });

  const importMut = useMutation({
    mutationFn: (a: SavedAddress) =>
      createFn({
        data: {
          label: "",
          fullName: a.fullName,
          phone: a.phone,
          line1: a.line1,
          line2: a.line2,
          city: a.city,
          postalCode: a.postalCode,
          region: "",
          country: "Ελλάδα",
          isDefault: false,
        },
      }),
    onSuccess: () => {
      invalidate();
      setForm(null);
      setError(null);
    },
    onError: () => {
      setError("Συμπλήρωσε τα στοιχεία που λείπουν (το τηλέφωνο χρειάζεται 10 ψηφία) και πάτησε Αποθήκευση.");
    },
  });

  // Όταν η διεύθυνση από τις παραγγελίες είναι πλήρης (έγκυρο ονοματεπώνυμο
  // & 10ψήφιο τηλέφωνο) την αποθηκεύουμε με ένα κλικ. Αλλιώς ανοίγουμε τη
  // φόρμα προσυμπληρωμένη ώστε ο χρήστης να συμπληρώσει τα στοιχεία που λείπουν.
  const handleImport = (a: SavedAddress) => {
    const validPhone = isValidContactPhone(a.phone);
    const validName = (a.fullName ?? "").trim().length >= 2;
    const validPostal = (a.postalCode ?? "").trim().length >= 3;
    if (validPhone && validName && validPostal) {
      setError(null);
      importMut.mutate(a);
      return;
    }
    setError(null);
    setForm({
      ...EMPTY,
      fullName: a.fullName ?? "",
      phone: a.phone ?? "",
      line1: a.line1 ?? "",
      line2: a.line2 ?? "",
      city: a.city ?? "",
      postalCode: a.postalCode ?? "",
    });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }
  };

  const set = (k: keyof FormState, v: string | boolean) =>
    setForm((p) => (p ? { ...p, [k]: v } : p));

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xl font-bold">📍 Οι διευθύνσεις μου</h2>
          {!form && (
            <button
              onClick={() => { setError(null); setForm({ ...EMPTY }); }}
              className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
            >
              + Νέα διεύθυνση
            </button>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Φόρτωση...</p>
        ) : addresses.length === 0 && !form ? (
          <div className="rounded-xl bg-muted/50 p-10 text-center">
            <div className="mb-2 text-4xl">🏠</div>
            <p className="text-sm text-muted-foreground">Δεν έχεις αποθηκευμένες διευθύνσεις.</p>
          </div>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {addresses.map((a) => (
              <li key={a.id} className="relative rounded-xl border border-border p-4">
                {a.isDefault && (
                  <span className="absolute right-3 top-3 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                    Προεπιλογή
                  </span>
                )}
                <div className="text-sm font-semibold">{a.label || a.fullName || "Διεύθυνση"}</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {a.fullName && <div>{a.fullName}</div>}
                  <div>{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
                  <div>{a.postalCode} {a.city}{a.region ? `, ${a.region}` : ""}</div>
                  <div>{a.country}</div>
                  {a.phone && <div>📞 {a.phone}</div>}
                </div>
                <div className="mt-3 flex gap-2 text-xs">
                  <button
                    onClick={() => { setError(null); setForm({ ...a }); }}
                    className="rounded-full border border-border px-3 py-1.5 transition hover:bg-muted"
                  >
                    Επεξεργασία
                  </button>
                  <button
                    onClick={() => delMut.mutate(a.id)}
                    className="rounded-full border border-border px-3 py-1.5 text-destructive transition hover:bg-destructive/10"
                  >
                    Διαγραφή
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {derived.length > 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-6">
          <div className="mb-1 flex items-center gap-2">
            <h3 className="text-base font-bold">📦 Από τις παραγγελίες σου</h3>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Βρήκαμε αυτές τις διευθύνσεις στις προηγούμενες παραγγελίες σου. Αποθήκευσέ τες με ένα κλικ.
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {derived.map((a, i) => (
              <li key={i} className="relative rounded-xl border border-border bg-card p-4">
                <div className="text-sm font-semibold">{a.fullName || "Διεύθυνση"}</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  <div>{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
                  <div>{a.postalCode} {a.city}</div>
                  {a.phone && <div>📞 {a.phone}</div>}
                </div>
                <button
                  onClick={() => handleImport(a)}
                  disabled={importMut.isPending}
                  className="mt-3 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
                >
                  + Αποθήκευση
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {form && (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold">{form.id ? "Επεξεργασία διεύθυνσης" : "Νέα διεύθυνση"}</h3>
          {error && (
            <p className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Ετικέτα (π.χ. Σπίτι)"><Input value={form.label} onChange={(v) => set("label", v)} /></Field>
            <Field label="Ονοματεπώνυμο*"><Input value={form.fullName} onChange={(v) => set("fullName", v)} /></Field>
            <Field label="Διεύθυνση*"><Input value={form.line1} onChange={(v) => set("line1", v)} /></Field>
            <Field label="Διεύθυνση (2η γραμμή)"><Input value={form.line2} onChange={(v) => set("line2", v)} /></Field>
            <Field label="Πόλη*"><Input value={form.city} onChange={(v) => set("city", v)} /></Field>
            <Field label="Τ.Κ.*"><Input value={form.postalCode} onChange={(v) => set("postalCode", v)} /></Field>
            <Field label="Νομός / Περιοχή"><Input value={form.region} onChange={(v) => set("region", v)} /></Field>
            <Field label="Χώρα"><Input value={form.country} onChange={(v) => set("country", v)} /></Field>
            <Field label="Τηλέφωνο*">
              <Input
                value={form.phone}
                onChange={(v) => set("phone", normalizePhoneDigits(v))}
                placeholder="π.χ. 6912345678"
              />
            </Field>
            <label className="flex items-center gap-2 self-end text-sm">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => set("isDefault", e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              Ορισμός ως προεπιλογή
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => saveMut.mutate(form)}
              disabled={saveMut.isPending}
              className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
            >
              {saveMut.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
            </button>
            <button
              onClick={() => { setForm(null); setError(null); }}
              className="rounded-full border border-border px-5 py-2 text-sm transition hover:bg-muted"
            >
              Ακύρωση
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
    />
  );
}
